/**
 * The agent being edited (design 11 §1.2, "one source of truth, two views"): one YAML document that
 * both the form and the YAML editor change. The form sets or removes one path at a time in the
 * document, so what it does not show — comments, key order, fields it has no control for — stays
 * exactly as written. The YAML view is the document's text.
 *
 * A SpecDocument is immutable: every change returns a new one, so React sees each edit.
 */
import { Document, isMap, isScalar, isSeq, LineCounter, parseDocument, type ParsedNode } from "yaml";

export type Path = readonly (string | number)[];

/** A problem in the YAML text, at a 1-based line and column. */
export interface YamlProblem {
  message: string;
  line: number;
  column: number;
}

export type Parsed = { ok: true; doc: SpecDocument } | { ok: false; problems: YamlProblem[] };

const STRINGIFY = { lineWidth: 0, indent: 2 } as const; // no folding: instructions stay as written

export class SpecDocument {
  private constructor(
    private readonly doc: Document<ParsedNode, true> | Document,
    readonly text: string,
    private readonly lines: LineCounter,
  ) {}

  /** A document from YAML the user wrote: kept as written, comments included. */
  static parse(text: string): Parsed {
    const lines = new LineCounter();
    const doc = parseDocument(text, { prettyErrors: false, uniqueKeys: true, lineCounter: lines });
    const problems = [...doc.errors].map((e) => {
      const { line, col } = lines.linePos(e.pos[0]);
      return { message: e.message.split("\n")[0] ?? e.message, line, column: col };
    });
    if (problems.length) return { ok: false, problems };
    if (doc.contents !== null && !isMap(doc.contents))
      return { ok: false, problems: [{ message: "An agent is a mapping: apiVersion, kind, metadata, spec", line: 1, column: 1 }] };
    return { ok: true, doc: new SpecDocument(doc, text, lines) };
  }

  /** The text, parsed again: every node knows where it is in it (for lineOf). */
  private static reparsed(text: string): SpecDocument {
    const parsed = SpecDocument.parse(text);
    if (!parsed.ok) throw new Error(`the YAML library wrote YAML it cannot read: ${parsed.problems[0]?.message}`);
    return parsed.doc;
  }

  /** A document from a value (a stored version): laid out the way the YAML view shows it. */
  static fromValue(value: unknown): SpecDocument {
    return SpecDocument.reparsed(new Document(value).toString(STRINGIFY));
  }

  /** The value at `path`, as plain data; undefined when it is not there. */
  get(path: Path): unknown {
    const node = this.doc.getIn(path, true);
    if (node === undefined || node === null) return undefined;
    return typeof node === "object" && "toJSON" in node ? (node as { toJSON: () => unknown }).toJSON() : node;
  }

  /**
   * The document with `path` set to `value`; undefined (or an empty string) removes it, unless
   * `keepEmpty` (a required field: it stays in place, empty, and validation says why). Nothing else
   * in the document changes: what is around the path keeps its comments and its layout.
   */
  set(path: Path, value: unknown, { keepEmpty = false }: { keepEmpty?: boolean } = {}): SpecDocument {
    const next = this.doc.clone();
    if (value === undefined || (value === "" && !keepEmpty)) {
      if (next.hasIn(path)) next.deleteIn(path);
      pruneEmptyParents(next, path);
    } else {
      // a null on the way (the API shows unset sections as null: realtime: null) becomes the mapping
      for (let i = 1; i < path.length; i++) {
        const above = next.getIn(path.slice(0, i), true);
        if (isScalar(above) && above.value === null) next.deleteIn(path.slice(0, i));
      }
      next.setIn(path, value);
      // setIn keeps the node's quoting, and a quote chosen for one keystroke ("Be " ends in a space)
      // would stick to every later one: a value from the form picks its quoting afresh. A block
      // scalar (instructions: |) stays one.
      const node = next.getIn(path, true);
      if (isScalar(node) && node.type !== "BLOCK_LITERAL" && node.type !== "BLOCK_FOLDED") node.type = undefined;
    }
    return SpecDocument.reparsed(next.toString(STRINGIFY));
  }

  /**
   * The document after `edit` changes a copy of it: several path edits as one change (the flow
   * canvas's edits). What `edit` does not touch keeps its comments and its layout.
   */
  change(edit: (doc: Document) => void): SpecDocument {
    const next = this.doc.clone() as Document;
    edit(next);
    return SpecDocument.reparsed(next.toString(STRINGIFY));
  }

  /**
   * The 1-based line `path` is on in the text; for a path that is not there (a missing required
   * field), the line of its nearest parent that is.
   */
  lineOf(path: Path): number {
    for (let end = path.length; end > 0; end--) {
      const node = this.doc.getIn(path.slice(0, end), true) as { range?: [number, number, number] } | undefined;
      const offset = this.keyOffset(path.slice(0, end)) ?? node?.range?.[0];
      if (offset !== undefined) return this.lines.linePos(offset).line;
    }
    return 1;
  }

  /** Where the key of the last path segment is (a mapping entry starts at its key, not its value). */
  private keyOffset(path: Path): number | undefined {
    const parent = path.length > 1 ? this.doc.getIn(path.slice(0, -1), true) : this.doc.contents;
    const last = path[path.length - 1];
    if (isMap(parent)) {
      const pair = parent.items.find((p) => (isScalar(p.key) ? p.key.value : p.key) === last);
      const key = pair?.key as { range?: [number, number, number] } | undefined;
      return key?.range?.[0];
    }
    if (isSeq(parent) && typeof last === "number") {
      const item = parent.items[last] as { range?: [number, number, number] } | undefined;
      return item?.range?.[0];
    }
    return undefined;
  }

  /** The document as data: what is sent to the API. */
  toJSON(): unknown {
    return this.doc.toJS({ maxAliasCount: 50 });
  }
}

/** Removing a form field's value leaves no `{}` behind that the user never wrote. */
function pruneEmptyParents(doc: Document, path: Path): void {
  for (let end = path.length - 1; end > 0; end--) {
    const parent = path.slice(0, end);
    const node = doc.getIn(parent, true);
    const empty = (isMap(node) || isSeq(node)) && node.items.length === 0;
    if (!empty) return;
    doc.deleteIn(parent);
  }
}
